import React, { useState } from 'react';

import { motion } from 'framer-motion';
import { BookOpen, ExternalLink, Info, PieChart as PieChartIcon } from 'lucide-react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { improvementSources, metricToSourceMap } from '../data/improvementSources';

interface ImprovementDistributionChartProps {
  className?: string;
}

const ImprovementDistributionChart: React.FC<ImprovementDistributionChartProps> = ({
  className = '',
}) => {
  // Estados para controlar la visualización de información
  const [activeMetric, setActiveMetric] = useState<string | null>(null);
  const [showSources, setShowSources] = useState<boolean>(false);
  const [hoverMetric, setHoverMetric] = useState<string | null>(null);

  // Datos mejorados para el gráfico circular - Distribución de mejoras
  const improvementData = [
    {
      name: 'Productividad',
      value: 35,
      description: 'Aumento en eficiencia operativa',
      icon: 'TrendingUp',
      impacto: 'Alto',
      tiempoImplementacion: '3-6 meses',
    },
    {
      name: 'Reducción Costos',
      value: 25,
      description: 'Disminución en gastos operativos',
      icon: 'DollarSign',
      impacto: 'Alto',
      tiempoImplementacion: '2-4 meses',
    },
    {
      name: 'Satisfacción Cliente',
      value: 20,
      description: 'Mejora en experiencia del usuario',
      icon: 'Heart',
      impacto: 'Medio-Alto',
      tiempoImplementacion: '1-3 meses',
    },
    {
      name: 'Innovación',
      value: 15,
      description: 'Nuevas oportunidades de negocio',
      icon: 'Lightbulb',
      impacto: 'Medio',
      tiempoImplementacion: '6-12 meses',
    },
    {
      name: 'Cumplimiento',
      value: 5,
      description: 'Mejora en normativas y seguridad',
      icon: 'Shield',
      impacto: 'Crítico',
      tiempoImplementacion: '1-2 meses',
    },
  ];

  // Paleta de colores mejorada para un aspecto más profesional
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6'];

  // Función para mostrar información detallada al hacer clic en una métrica
  const handleMetricClick = (metricName: string) => {
    setActiveMetric(activeMetric === metricName ? null : metricName);
  };

  // Función para controlar el estado de hover
  const handleMetricHover = (metricName: string | null) => {
    setHoverMetric(metricName);
  };

  // Información detallada y enriquecida para cada métrica
  const metricDetails = {
    Productividad:
      'El aumento de productividad del 35% se traduce en completar más tareas en menos tiempo. Nuestros clientes reportan que procesos que antes tomaban 10 horas ahora se completan en 6.5 horas, liberando recursos para actividades de mayor valor. Este incremento se refleja en una mejora del 42% en la capacidad de respuesta y un 28% en la calidad del trabajo entregado.',
    'Reducción Costos':
      'La reducción de costos operativos del 25% incluye ahorros en personal (10%), infraestructura (8%) y procesos manuales (7%). Para una empresa con gastos operativos de $100,000 mensuales, esto representa un ahorro anual de $300,000. El ROI promedio de esta inversión se alcanza en tan solo 4 meses, con beneficios sostenidos a largo plazo.',
    'Satisfacción Cliente':
      'El 20% de mejora en satisfacción del cliente se refleja en un aumento del NPS (Net Promoter Score) de 45 a 72 puntos. Esto ha resultado en un incremento del 40% en recomendaciones y referencias de nuevos clientes. Los estudios muestran que cada punto de mejora en el NPS se traduce en aproximadamente un 0.5% de crecimiento en ingresos anuales.',
    Innovación:
      'El componente de innovación (15%) ha permitido a nuestros clientes desarrollar un promedio de 3 nuevos productos o servicios por año, generando nuevas fuentes de ingresos que representan hasta un 22% de crecimiento en ventas. Las empresas que priorizan la innovación superan en rendimiento a sus competidores en un 30% durante períodos de 5 años.',
    Cumplimiento:
      'La mejora en cumplimiento normativo y seguridad (5%) ha reducido incidentes de seguridad en un 80% y ha eliminado completamente las multas por incumplimiento regulatorio, que en promedio costaban a las empresas $50,000 anuales. Además, ha fortalecido la confianza de los clientes, con un 65% reportando mayor disposición a compartir datos con empresas que demuestran sólidas prácticas de seguridad.',
  };

  return (
    <section className={`py-16 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Distribución de Mejoras</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-4">
            Análisis detallado de cómo nuestras soluciones impactan diferentes áreas de su negocio,
            generando mejoras cuantificables y sostenibles a largo plazo.
          </p>
          <p className="text-md text-gray-700 max-w-2xl mx-auto italic border-l-4 border-blue-400 pl-4 py-2 bg-blue-50">
            Cada área de su negocio experimenta mejoras específicas y medibles con nuestras
            soluciones exclusivas. Este análisis muestra la distribución del impacto basado en datos
            reales de implementaciones exitosas.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-gray-50 to-blue-50 p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
          >
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-xl font-semibold text-gray-800 flex items-center">
                <PieChartIcon className="h-5 w-5 mr-2 text-blue-600" />
                Distribución de Mejoras (%)
              </h4>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowSources(!showSources)}
                  className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full flex items-center hover:bg-blue-200 transition-colors"
                >
                  <BookOpen className="h-3 w-3 mr-1" />
                  {showSources ? 'Ocultar fuentes' : 'Ver fuentes de datos'}
                </button>
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full flex items-center">
                  <PieChartIcon className="h-3 w-3 mr-1" /> Datos validados 2023
                </span>
              </div>
            </div>

            {/* Panel de fuentes de datos */}
            {showSources && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100"
              >
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-sm font-semibold text-blue-800 flex items-center">
                    <Info className="h-4 w-4 mr-1" /> Fuentes de datos utilizadas
                  </h5>
                  <button
                    onClick={() => setShowSources(false)}
                    className="text-blue-500 hover:text-blue-700 text-xs"
                  >
                    Cerrar
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {improvementSources.map((source, index) => (
                    <div
                      key={index}
                      className="bg-white p-3 rounded-md shadow-sm border border-blue-100"
                    >
                      <div className="flex justify-between items-start">
                        <h6 className="text-sm font-medium text-gray-800">{source.name}</h6>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded">
                          {source.year}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 mb-2">{source.description}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">{source.organization}</span>
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          Verificar fuente <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="h-96">
                {' '}
                {/* Altura aumentada para mejor visualización */}
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={improvementData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={90}
                      innerRadius={30} // Añadido para crear un gráfico de dona
                      paddingAngle={2} // Añadido para separar los segmentos
                      fill="#8884d8"
                      dataKey="value"
                      onClick={data => handleMetricClick(data.name)}
                      onMouseEnter={data => handleMetricHover(data.name)}
                      onMouseLeave={() => handleMetricHover(null)}
                      animationBegin={0}
                      animationDuration={1500}
                      animationEasing="ease-out"
                    >
                      {improvementData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                          stroke={
                            activeMetric === entry.name || hoverMetric === entry.name
                              ? '#fff'
                              : 'transparent'
                          }
                          strokeWidth={
                            activeMetric === entry.name || hoverMetric === entry.name ? 2 : 0
                          }
                          style={{
                            filter:
                              activeMetric === entry.name
                                ? 'drop-shadow(0px 0px 6px rgba(0,0,0,0.3))'
                                : 'none',
                          }}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={value => [`${value}%`, 'Porcentaje']}
                      labelFormatter={name => {
                        const item = improvementData.find(item => item.name === name);
                        return (
                          <div className="font-medium text-gray-800">
                            {name}
                            <div className="text-xs font-normal mt-1 text-gray-600">
                              {item?.description}
                            </div>
                            <div className="text-xs font-normal mt-1 text-gray-600">
                              Impacto: {item?.impacto}
                            </div>
                            <div className="text-xs font-normal mt-1 text-gray-600">
                              Tiempo: {item?.tiempoImplementacion}
                            </div>
                          </div>
                        );
                      }}
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Legend
                      layout="vertical"
                      verticalAlign="middle"
                      align="right"
                      wrapperStyle={{ paddingLeft: '20px' }}
                      formatter={value => {
                        return (
                          <span className="text-sm font-medium text-gray-700 cursor-pointer hover:text-blue-600 transition-colors">
                            {value}
                          </span>
                        );
                      }}
                      onClick={data => handleMetricClick(data.value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="flex flex-col justify-center">
                <h5 className="text-lg font-semibold text-gray-800 mb-4">
                  Distribución del Impacto
                </h5>
                <p className="text-sm text-gray-600 mb-4">
                  Este gráfico muestra cómo nuestras soluciones generan mejoras en diferentes áreas
                  de su negocio. Cada segmento representa el porcentaje de impacto en un área
                  específica, basado en datos recopilados de más de 200 implementaciones exitosas.
                </p>

                <div className="grid grid-cols-1 gap-2">
                  {improvementData.map((item, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ x: 5 }}
                      className={`p-2 rounded-md cursor-pointer flex items-center ${activeMetric === item.name ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'}`}
                      onClick={() => handleMetricClick(item.name)}
                    >
                      <div
                        className="h-4 w-4 rounded-full mr-2"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <div>
                        <span className="text-sm font-medium text-gray-800">
                          {item.name}: {item.value}%
                        </span>
                        <p className="text-xs text-gray-500">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-4 text-xs text-gray-500">
                  <p>Haga clic en los segmentos o leyendas para ver detalles</p>
                </div>
              </div>
            </div>

            {/* Panel de información detallada mejorado */}
            {activeMetric && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="mt-6 p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                <div className="flex items-start">
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0"
                    style={{
                      backgroundColor:
                        COLORS[
                          improvementData.findIndex(item => item.name === activeMetric) %
                            COLORS.length
                        ],
                      opacity: 0.8,
                    }}
                  >
                    <Info className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-semibold text-gray-800 text-lg">{activeMetric}</h5>
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                        {improvementData.find(item => item.name === activeMetric)?.value}% del
                        impacto total
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div className="bg-gray-50 p-2 rounded border border-gray-200">
                        <span className="text-xs text-gray-500 block">Nivel de impacto:</span>
                        <span className="text-sm font-medium text-gray-800">
                          {improvementData.find(item => item.name === activeMetric)?.impacto}
                        </span>
                      </div>
                      <div className="bg-gray-50 p-2 rounded border border-gray-200">
                        <span className="text-xs text-gray-500 block">
                          Tiempo de implementación:
                        </span>
                        <span className="text-sm font-medium text-gray-800">
                          {
                            improvementData.find(item => item.name === activeMetric)
                              ?.tiempoImplementacion
                          }
                        </span>
                      </div>
                      <div className="bg-gray-50 p-2 rounded border border-gray-200">
                        <span className="text-xs text-gray-500 block">Fuente de datos:</span>
                        <span className="text-sm font-medium text-gray-800">
                          {metricToSourceMap[activeMetric]?.[0] || 'Múltiples fuentes'}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 leading-relaxed">
                      {metricDetails[activeMetric as keyof typeof metricDetails]}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ImprovementDistributionChart;
